function normalizeFeatureKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function mergeAccessValue(acc, rawKey, rawValue) {
  const key = normalizeFeatureKey(rawKey);
  if (!key) return;

  const value = Boolean(rawValue);
  acc[key] = acc[key] === true || value;
}

function entitlementItemEnabled(item) {
  return Boolean(item?.enabled || item?.status === "enabled");
}

function resourceItemEnabled(item) {
  return Boolean(
    item?.enabled ||
    item?.unlimited ||
    Number(item?.limit || 0) > 0 ||
    Number(item?.base || 0) > 0 ||
    Number(item?.addons || 0) > 0
  );
}

export function parseFeatureAccessFromEntitlements(entitlementsResponse) {
  const entitlements = entitlementsResponse?.entitlements || entitlementsResponse;
  if (!entitlements || typeof entitlements !== "object") {
    return null;
  }

  const mapped = {};

  [
    ...(Array.isArray(entitlements.products) ? entitlements.products : []),
    ...(Array.isArray(entitlements.modules) ? entitlements.modules : []),
    ...(Array.isArray(entitlements.capabilities) ? entitlements.capabilities : []),
  ].forEach((item) => {
    mergeAccessValue(mapped, item?.key, entitlementItemEnabled(item));
  });

  (Array.isArray(entitlements.resources) ? entitlements.resources : []).forEach((item) => {
    mergeAccessValue(mapped, item?.key, resourceItemEnabled(item));
  });

  return Object.keys(mapped).length > 0 ? mapped : null;
}

export function isFeatureAllowed(featureAccess, keys) {
  if (!featureAccess || typeof featureAccess !== "object") {
    return false;
  }

  const candidates = Array.isArray(keys) ? keys : [keys];
  if (candidates.length === 0) {
    return false;
  }

  return candidates.some((candidate) => {
    const key = normalizeFeatureKey(candidate);
    if (!key) return false;
    return featureAccess[key] === true;
  });
}

export function areFeaturesAllowed(featureAccess, keys) {
  if (!featureAccess || typeof featureAccess !== "object") {
    return false;
  }

  const candidates = Array.isArray(keys) ? keys : [keys];
  if (candidates.length === 0) {
    return false;
  }

  return candidates.every((candidate) => {
    const key = normalizeFeatureKey(candidate);
    if (!key) return false;
    return featureAccess[key] === true;
  });
}

const PERMISSION_ACTION_BY_MODULE_KEY = {
  campaigns: {
    create: "campaigns.create",
    edit: "campaigns.edit",
    reports: "campaigns.reports",
    resend: "campaigns.resend",
    status: "campaigns.status",
    view: "campaigns.view",
    visibility: "campaigns.visibility",
  },
  audiences: {
    create: "audiences.manage",
    edit: "audiences.manage",
    manage: "audiences.manage",
    use: "audiences.use",
    view: "audiences.use",
  },
  message_templates: {
    create: "message_templates.manage",
    edit: "message_templates.manage",
    manage: "message_templates.manage",
    use: "message_templates.use",
    view: "message_templates.use",
  },
  channels: {
    create: "channels.manage",
    edit: "channels.manage",
    manage: "channels.manage",
    use: "channels.use",
    view: "channels.use",
  },
  integrations: {
    create: "integrations.manage",
    edit: "integrations.manage",
    manage: "integrations.manage",
    use: "integrations.use",
    view: "integrations.use",
  },
  account_settings: {
    edit: "account_settings.manage",
    manage: "account_settings.manage",
    use: "account_settings.use",
    view: "account_settings.use",
  },
  account_users: {
    create: "account_users.manage",
    edit: "account_users.manage",
    manage: "account_users.manage",
    use: "account_users.use",
    view: "account_users.use",
  },
  account_features: {
    edit: "account_features.manage",
    manage: "account_features.manage",
    use: "account_features.use",
    view: "account_features.use",
  },
};

const PERMISSION_LEVEL_ACTIONS = {
  campaigns: {
    manager: new Set([
      "campaigns.create",
      "campaigns.edit",
      "campaigns.reports",
      "campaigns.resend",
      "campaigns.status",
      "campaigns.view",
      "campaigns.visibility",
    ]),
    creator: new Set([
      "campaigns.create",
      "campaigns.edit",
      "campaigns.view",
    ]),
    none: new Set(),
  },
  audiences: {
    manager: new Set(["audiences.manage", "audiences.use"]),
    viewer: new Set(["audiences.use"]),
    none: new Set(),
  },
  message_templates: {
    manager: new Set(["message_templates.manage", "message_templates.use"]),
    viewer: new Set(["message_templates.use"]),
    none: new Set(),
  },
  channels: {
    manager: new Set(["channels.manage", "channels.use"]),
    viewer: new Set(["channels.use"]),
    none: new Set(),
  },
  integrations: {
    manager: new Set(["integrations.manage", "integrations.use"]),
    viewer: new Set(["integrations.use"]),
    none: new Set(),
  },
  account_settings: {
    manager: new Set(["account_settings.manage", "account_settings.use"]),
    viewer: new Set(["account_settings.use"]),
    none: new Set(),
  },
  account_users: {
    manager: new Set(["account_users.manage", "account_users.use"]),
    viewer: new Set(["account_users.use"]),
    none: new Set(),
  },
  account_features: {
    manager: new Set(["account_features.manage", "account_features.use"]),
    viewer: new Set(["account_features.use"]),
    none: new Set(),
  },
};

function normalizePermissionModule(value) {
  const moduleKey = String(value || "").trim().toLowerCase().replace(/-/g, "_");
  return Object.prototype.hasOwnProperty.call(PERMISSION_LEVEL_ACTIONS, moduleKey)
    ? moduleKey
    : "";
}

function normalizePermissionLevel(module, value) {
  const moduleKey = normalizePermissionModule(module);
  const level = String(value || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(PERMISSION_LEVEL_ACTIONS[moduleKey] || {}, level)
    ? level
    : "";
}

function resolvePermissions(user) {
  return user?.permissions && typeof user.permissions === "object"
    ? user.permissions
    : {};
}

function resolvePermissionSlugs(user) {
  const permissions = resolvePermissions(user);
  const slugs = permissions?.permission_slugs || user?.permission_slugs;
  return Array.isArray(slugs) && slugs.length > 0 ? slugs : null;
}

function hasLoadedPermissions(user) {
  return Boolean(
    user?.permissionsLoaded ||
    user?.permissions_loaded ||
    user?.permissions?.permissionsLoaded ||
    user?.permissions?.permissions_loaded
  );
}

function isAccountOwner(user) {
  return Boolean(
    user?.is_account_owner ||
    user?.isAccountOwner ||
    user?.permissions?.is_account_owner ||
    user?.permissions?.isAccountOwner
  );
}

export function hasKnownPermissions(user, module) {
  const moduleKey = normalizePermissionModule(module);
  if (!moduleKey) return false;

  const permissions = resolvePermissions(user);
  const level = normalizePermissionLevel(
    moduleKey,
    permissions?.permission_levels?.[moduleKey] ||
    user?.permission_levels?.[moduleKey],
  );

  return Boolean(
    isAccountOwner(user) ||
    hasLoadedPermissions(user) ||
    level ||
    permissions?.capabilities?.[moduleKey] ||
    user?.capabilities?.[moduleKey] ||
    resolvePermissionSlugs(user),
  );
}

export function hasKnownCampaignPermissions(user) {
  return hasKnownPermissions(user, "campaigns");
}

export function canAccessPermission(user, module, action = "view") {
  const moduleKey = normalizePermissionModule(module);
  if (!moduleKey) return false;

  const permissionAction =
    PERMISSION_ACTION_BY_MODULE_KEY[moduleKey]?.[action] ||
    (String(action || "").startsWith(`${moduleKey}.`) ? String(action) : `${moduleKey}.${action}`);
  const permissions = resolvePermissions(user);
  const capabilityActions =
    permissions?.capabilities?.[moduleKey]?.actions ||
    user?.capabilities?.[moduleKey]?.actions;

  if (isAccountOwner(user)) {
    return true;
  }

  if (Array.isArray(capabilityActions)) {
    return capabilityActions.includes(permissionAction);
  }

  const level = normalizePermissionLevel(
    moduleKey,
    permissions?.permission_levels?.[moduleKey] ||
    user?.permission_levels?.[moduleKey],
  );

  if (level) {
    return PERMISSION_LEVEL_ACTIONS[moduleKey][level].has(permissionAction);
  }

  const slugs = resolvePermissionSlugs(user);
  if (slugs) {
    if (slugs.includes(`${moduleKey}.none`)) {
      return false;
    }

    if (slugs.includes(`${moduleKey}.manager`)) {
      return PERMISSION_LEVEL_ACTIONS[moduleKey].manager.has(permissionAction);
    }

    if (slugs.includes(`${moduleKey}.creator`)) {
      return PERMISSION_LEVEL_ACTIONS[moduleKey].creator?.has(permissionAction) === true;
    }

    if (slugs.includes(`${moduleKey}.viewer`)) {
      return PERMISSION_LEVEL_ACTIONS[moduleKey].viewer?.has(permissionAction) === true;
    }

    return false;
  }

  return false;
}

export function canAccessCampaigns(user, action = "view") {
  return canAccessPermission(user, "campaigns", action);
}
